import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContentSectionInfoRow} from '#/components/actionmenu/subcomponents';
import {LandGraphData} from '#/components/graphs/use-land-graph-data';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {ClassName} from '#/utils/utils';
import i18next from 'i18next';
import {FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

type DataGroupInformation = {name: string; color: string};

type LineKeys = keyof Pick<LandGraphData, 'oraclePrice' | 'marketPrice'>;

export const DateValueLineBarGraph: FunctionComponent<
  {
    data: LandGraphData[];
    activeDataKeys: Omit<keyof LandGraphData, 'date'>[];
    yLineTicks: number[];
    graphHeight: number;
    graphMargins: {top: number; right: number; bottom: number; left: number};
    barHeight: number;
    barPadding: number;
  } & ClassName
> = ({
  className = '',
  data,
  activeDataKeys,
  yLineTicks,
  graphHeight,
  graphMargins = {top: 10, right: 10, bottom: 5, left: 10},
  barHeight = Math.round(graphHeight / 3.6),
  barPadding = graphMargins.top,
}) => {
  const {t} = useTranslation('translation');

  const lines = {
    oraclePrice: {
      name: 'Real World Price',
      color: '#90EE79',
    },
    marketPrice: {
      name: 'Market Price',
      color: '#3182ce',
    },
  };
  const bar = {
    name: 'Volume',
    color: '#FFA500',
  };

  const lineKeys = Object.keys(lines) as LineKeys[];
  const barKey = 'volume';

  const barActive = activeDataKeys.includes(barKey);
  const linesActive = activeDataKeys.some(val => lineKeys.includes(val as LineKeys));

  const isStable = yLineTicks.length === 1;
  const finalLineTicks = isStable
    ? [yLineTicks[0] - 2, yLineTicks[0] - 1, yLineTicks[0], yLineTicks[0] + 1, yLineTicks[0] + 2]
    : yLineTicks;

  const yTickFormatter = (value: number, index: number) =>
    t('components.dateValueLineBarGraph.tickFormatter.yLine', {
      context: isStable && index !== 2 ? 'empty' : 'value',
      value,
    });
  const yTickFormatterBar = (value: number) =>
    t('components.dateValueLineBarGraph.tickFormatter.yBar', {
      value,
    });

  return (
    <ResponsiveContainer width='100%' height={graphHeight} className={className}>
      <ComposedChart data={data} margin={graphMargins}>
        <CartesianGrid horizontal={true} vertical={false} stroke='white' opacity={0.5} strokeWidth={0.3} />

        <XAxis dataKey={'date'} scale='time' type='number' domain={['dataMin', 'dataMax']} hide={true} />

        <YAxis
          yAxisId='0'
          orientation='left'
          domain={[finalLineTicks[0], finalLineTicks[yLineTicks.length - 1]]}
          ticks={finalLineTicks}
          tickFormatter={yTickFormatter}
          tick={{stroke: 'white', strokeWidth: 0.1, fontSize: 12, opacity: 0.5}}
          strokeWidth='0'
          stroke='white'
          tickMargin={10}
          interval={0}
          padding={{bottom: activeDataKeys.includes(barKey) ? barHeight : 0}}
          hide={!linesActive}
        />

        <YAxis
          yAxisId='1'
          orientation='left'
          tick={{stroke: 'white', strokeWidth: 0.1, fontSize: 12, opacity: 0.5}}
          strokeWidth='0'
          stroke='white'
          tickMargin={0}
          tickFormatter={yTickFormatterBar}
          interval={0}
          padding={{
            top: linesActive ? graphHeight - graphMargins.top - graphMargins.bottom - barHeight + barPadding : 0,
          }}
          hide={linesActive}
        />

        {Object.entries(lines).map(([key, mod], index) => {
          const lineKey = key as LineKeys;
          const line = mod as DataGroupInformation;

          return (
            <Line
              key={index}
              name={line.name}
              dataKey={lineKey as string}
              stroke={line.color}
              type='linear'
              yAxisId='0'
              strokeWidth={3}
              strokeLinejoin='round'
              strokeLinecap='round'
              connectNulls={true}
              dot={false}
              isAnimationActive={false}
              hide={!activeDataKeys.includes(lineKey)}
            />
          );
        })}

        {barActive && (
          <Bar
            name={bar.name}
            dataKey={barKey as string}
            yAxisId='1'
            fill={bar.color}
            opacity={0.7}
            isAnimationActive={false}
            maxBarSize={15}
          />
        )}

        <Tooltip content={<LandMarketTickerTooltip />} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const tooltipKeys = i18next.t('components.dateValueLineBarGraph.tooltip.data', {
  ns: 'translation',
  returnObjects: true,
});

const LandMarketTickerTooltip: FunctionComponent<TooltipProps<number, string>> = ({active, payload, label}) => {
  const {t} = useTranslation('translation');

  const displayPref = useCoinDisplayPreferences();

  if (active && payload && payload.length && displayPref) {
    return (
      <div className='text-white font-semibold rounded-xl space-y-2 bg-black p-4 outline-none pointer-events-none'>
        <div className='text-xs text-white font-semibold'>
          {t('components.dateValueLineBarGraph.tooltip.date.value', {
            date: new Date(label),
          })}
        </div>

        <VerticalSeparator />

        {payload.map((payloadEntry, index) => {
          const dataKey = payloadEntry.dataKey as keyof typeof tooltipKeys;

          return (
            <ActionMenuContentSectionInfoRow
              key={index}
              children={{
                left: t(`components.dateValueLineBarGraph.tooltip.data.${dataKey}.title`),
                right: (
                  <span className='pl-10'>
                    {t(`components.dateValueLineBarGraph.tooltip.data.${dataKey}.value`, {
                      currencySymbol: displayPref.symbol,
                      amount: (payloadEntry.value ?? 0) * decimalsMultiplier,
                    })}
                  </span>
                ),
              }}
            />
          );
        })}
      </div>
    );
  }

  return null;
};
